import {
  InputOutputSection,
  InputOutputSelector,
} from '@modules/input-output-devices/InputOutputSelector';
import { alpha, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import cn from 'classnames';
import DetectRTC from 'detectrtc';
import { useCallback, useMemo } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import {
  ActionButton,
  StyledIcon,
} from 'src/components/ActionButton/ActionButton';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { VerticalDivider } from 'src/components/VerticalDivider/VerticalDivider';
import { AiGlow } from 'src/components/ai/AiGlow';
import { useSnackbar } from 'src/components/snackbar';
import { SMALL_MOBILE_BREAKPOINT } from 'src/config-global';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLocales } from 'src/locales';
import { useVoiceChannelContext } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { answerAudio, stopAnswerAudio } from 'src/requests/ai';
import { AskToAiButton } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelControls/Buttons/AskToAiButton/AskToAiButton';
import { useVoiceChannelInviteModal } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelInvite/VoiceChannelInviteModal';
import { useSocketIO } from 'src/socket';
import { getImageFromVideo } from 'src/utils/getImageFromVideo';
import { AiStopButton } from './Buttons/AskToAiButton';
import { useAudioStream } from './Buttons/AskToAiButton/useAudioStream';
import { SettingsActionButton } from './Buttons/SettingsActionButton';
import { TranscriptionNotesButton } from './Buttons/TranscriptionNotesButton';
import { VoiceControlSettingsPopover } from './Popovers/VoiceControlSettingsPopover';

type Props = {
  floating: boolean;
  isP2P?: boolean;
  isGuest?: boolean;
};

export function VoiceChannelControls({ floating, isP2P, isGuest }: Props) {
  const theme = useTheme();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { io } = useSocketIO();

  const { user } = useAuthContext();

  const { hasCamera, hasMicrophone, grantPermission } =
    useGatheringPermissions();

  const {
    disconnectVoiceChannel,
    setMuted,
    setDeafed,
    startScreenShare,
    stopScreenShare,
    startCamera,
    stopCamera,
  } = useVoiceChannelContext();

  const {
    activeVoiceChannel,
    connectionStatus,
    localParticipant,
    myMeetId,
    aiStatus,
    setAiStatus,
    channelLockedBy,
    viewMode,
    toggleHandRaised,
    setViewMode,
  } = useLivekitRTCState();

  const { audioState } = useAudioStream({
    socket: io,
    onError: (error) => {
      console.error(error);
    },
    onPlaying() {
      setAiStatus('speaking');
    },
    onStopped() {
      setAiStatus(null);
    },
  });

  const { inviteUsers } = useVoiceChannelInviteModal();

  const isConnectedToVoiceChannel = connectionStatus === 'connected';

  const askPermission = async () => {
    await grantPermission(DetectRTC.hasWebcam);
  };

  const handleAskToAI = async (file: File) => {
    if (!myMeetId) return;
    const image = getImageFromVideo(myMeetId);
    try {
      await answerAudio(activeVoiceChannel.channel._id, file, image);
    } catch (e) {
      io.emit(`/voice-channel/unlock-ai`, {
        channelId: activeVoiceChannel.channel._id,
      });
      enqueueSnackbar('Failed to ask ai. Try again.', {
        variant: 'error',
      });
    }
  };

  const viewGridIcon: {
    action: 'grid' | 'list';
    icon: string;
    tooltip: string;
  } = useMemo(
    () =>
      viewMode === 'grid'
        ? {
            action: 'list',
            icon: 'tabler:layout-list',
            tooltip: translate('communicate.rtc_controls.toggle_list'),
          }
        : {
            action: 'grid',
            icon: 'tabler:layout-grid',
            tooltip: translate('communicate.rtc_controls.toggle_grid'),
          },
    [viewMode, translate],
  );

  const handleStopAskAi = useCallback(() => {
    stopAnswerAudio(activeVoiceChannel.channel._id);
  }, [activeVoiceChannel.channel._id]);

  if (!isConnectedToVoiceChannel) return null;

  return (
    <>
      <Container className={cn({ floating, isP2P })}>
        {!isGuest && (
          <Stack
            direction="row"
            gap={2}
            alignItems="center"
            className="only-web"
          >
            {audioState.isPlaying && audioState.userId === user?._id ? (
              <AiStopButton onClick={handleStopAskAi} />
            ) : (
              <AskToAiButton onSend={handleAskToAI} />
            )}

            {!isP2P && channelLockedBy !== myMeetId && (
              <>
                <TranscriptionNotesButton />

                <ActionButton
                  onClick={inviteUsers}
                  color="inherit"
                  tooltip={translate('communicate.rtc_controls.invite_users')}
                >
                  <StyledIcon icon="tabler:users-plus" />
                </ActionButton>
              </>
            )}

            <VerticalDivider height={40} color={theme.palette.divider} />
          </Stack>
        )}

        <SettingsActionButton
          popover={InputOutputSelector}
          context={{
            showSections: [InputOutputSection.MICROPHONE],
          }}
          onClick={() => setMuted(!localParticipant.muted)}
          color={localParticipant.muted ? 'error' : 'success'}
          tooltip={
            localParticipant.muted
              ? translate('communicate.rtc_controls.unmute_microphone')
              : translate('communicate.rtc_controls.mute_microphone')
          }
          hasPermission={hasMicrophone}
          askPermission={askPermission}
        >
          <StyledIcon
            icon={
              localParticipant.muted
                ? 'tabler:microphone-off'
                : 'tabler:microphone'
            }
          />
        </SettingsActionButton>

        <SettingsActionButton
          popover={InputOutputSelector}
          context={{
            showSections: [InputOutputSection.SPEAKER],
          }}
          onClick={() => setDeafed(!localParticipant.deaf)}
          color={localParticipant.deaf ? 'error' : 'inherit'}
          tooltip={
            localParticipant.deaf
              ? translate('communicate.rtc_controls.unmute_speaker')
              : translate('communicate.rtc_controls.mute_speaker')
          }
          hasPermission={hasMicrophone}
          askPermission={askPermission}
        >
          <StyledIcon
            icon={
              localParticipant.deaf
                ? 'tabler:headphones-off'
                : 'tabler:headphones'
            }
          />
        </SettingsActionButton>

        {channelLockedBy !== myMeetId && (
          <>
            <SettingsActionButton
              popover={InputOutputSelector}
              context={{
                showSections: [InputOutputSection.CAMERA],
              }}
              onClick={() =>
                localParticipant.camera ? stopCamera() : startCamera()
              }
              color={localParticipant.camera ? 'success' : 'error'}
              tooltip={
                localParticipant.camera
                  ? translate('communicate.rtc_controls.hide_camera')
                  : translate('communicate.rtc_controls.show_camera')
              }
              hasPermission={hasCamera}
              askPermission={askPermission}
            >
              <StyledIcon
                icon={
                  localParticipant.camera ? 'tabler:video' : 'tabler:video-off'
                }
              />
            </SettingsActionButton>

            <ActionButton
              onClick={() =>
                localParticipant.sharing
                  ? stopScreenShare()
                  : startScreenShare()
              }
              color={localParticipant.sharing ? 'success' : 'inherit'}
              tooltip={
                localParticipant.sharing
                  ? translate('communicate.rtc_controls.stop_screen_share')
                  : translate('communicate.rtc_controls.screen_share')
              }
            >
              <StyledIcon icon="tabler:screen-share" />
            </ActionButton>

            <ActionButton
              onClick={() => toggleHandRaised()}
              color={localParticipant.raisedHand ? 'warning' : 'inherit'}
              tooltip={
                localParticipant.raisedHand
                  ? translate('communicate.rtc_controls.lower_hand')
                  : translate('communicate.rtc_controls.raise_hand')
              }
            >
              <StyledIcon icon="tabler:hand-stop" />
            </ActionButton>

            <ActionButton
              onClick={() => setViewMode(viewGridIcon.action)}
              tooltip={viewGridIcon.tooltip}
              color="inherit"
              className="only-web"
            >
              <StyledIcon icon={viewGridIcon.icon} />
            </ActionButton>

            <PopoverTrigger
              popover={VoiceControlSettingsPopover}
              context={{ isGuest, inviteUsers }}
            >
              <StyledDotsButton
                color="inherit"
                tooltip={translate('communicate.rtc_controls.voice_settings')}
              >
                <StyledIcon icon="tabler:dots-vertical" />
              </StyledDotsButton>
            </PopoverTrigger>
          </>
        )}

        <ActionButton
          onClick={() => disconnectVoiceChannel()}
          color="error"
          variant="contained"
          tooltip={translate('communicate.rtc_controls.hang_up')}
        >
          <StyledIcon icon="tabler:phone-x" />
        </ActionButton>
      </Container>
      {(aiStatus === 'recording' || aiStatus === 'speaking') && (
        <StyledAiGlow variant={aiStatus} />
      )}
    </>
  );
}

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.support,
  borderTop: `1px solid ${theme.palette.divider}`,
  zIndex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  width: '100vw',
  '&.floating': {
    border: 'none',
    backgroundColor: theme.palette.background.default,
  },
  '&.isP2P': {
    backgroundColor: alpha(theme.palette.grey[900], 0.48),
    '& .MuiButton-softInherit ': {
      color: theme.palette.common.white,
    },
    '& .options-button': {
      backgroundColor: theme.palette.grey[800],
      color: theme.palette.common.white,
      '& .popover-button': {
        color: theme.palette.grey[600],

        '&:hover svg': {
          color: theme.palette.common.white,
        },
      },
    },
  },
  '& .only-web': {
    display: 'none',
  },
  [theme.breakpoints.up(SMALL_MOBILE_BREAKPOINT)]: {
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    justifyContent: 'center',
    width: 'unset',
    '& .only-web': {
      display: 'inline-flex',
    },
  },
}));

const StyledAiGlow = styled(AiGlow)(() => ({
  position: 'absolute',
  zIndex: 0,
}));

const StyledDotsButton = styled(ActionButton)(({ theme }) => ({
  '& .MuiButton-root': {
    width: theme.spacing(3),
    padding: theme.spacing(0.5, 0.25),
  },
}));
